import { IconButton, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  selectFeatureFlagAgentCart,
  selectIsHitl7Enabled,
} from '../../redux/features/config/config.selector';
import { selectIsCarPresent } from '../../selectors/message';
import useAISuggestionActions from '../AISuggestions/useAISuggestionActions.hooks';
import useEventListener from '../../hooks/useEventListener.hooks';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';

/**
 * PULSE ADVISE (HITL 3.0) — the AI co-pilot's recommended ask, folded into the
 * Pulse strip from the old Action Center: the suggestion text with Accept /
 * Dismiss (tick / cross) on one row.
 *
 * Gated by the agent_cart flag (same master switch as the standalone suggestion
 * surface). Accept / Dismiss and the Alt+A / Alt+D shortcuts drive the shared
 * useAISuggestionActions path. Deliberately omits the old "AI recommends you
 * ask:" label and the reason line; the slash-command reminder now lives in
 * PulseSignals. Renders nothing when there's no active suggestion.
 */
// ── HITL 7.0 inline suggestion (HITL-1668) ───────────────────────────────────
// The ask leaves the Copilot strip and renders as the last child of the
// transcript, where it reads as the next thing that could be said rather than
// as a notice parked beside the conversation it is answering.
//
// Inline styles, not new makeStyles rules: this repo's JSS class names come
// from one global counter, so a new rule renumbers class names in unrelated
// snapshot suites. The 3.0 rules below are untouched.

// Grey, NOT a brand hue: the box is not a third participant in the conversation,
// it is a control the console is offering. Amber was the original runner-up (it
// already means "the AI put this here, you confirm") but it reads as a fourth
// voice. A navy fill was tried and rejected too — at full strength behind the
// text it overpowered the transcript around it.
const HITL7_FILL = Colors.porcelain;

// The header's own colour for the text and the badge, so the box belongs to the
// same console as the header and the Order Flow pills rather than carrying a
// blue of its own. 14:1 against the grey fill, and 15.6:1 for the white badge
// glyph sitting on it.
//
// NOTE for whoever revisits this: the original design paired this grey fill with
// a blue arrival flash, on the explicit reasoning that grey costs arrival
// salience and the flash bought it back. The flash has since been removed by
// decision — it read as a background changing on its own while the agent was
// reading. So the grey now stands WITHOUT the compensation it was granted on
// condition of. If suggestions turn out to be missed in the field, that pairing
// is the first thing to re-examine.
const HITL7_COPILOT_COLOR = Colors.daintree;

// 16px, and that number is load-bearing. The badge is centred ON the left
// border, so it overhangs by half its width, and the overhang is capped at the
// transcript's 8px padding: `overflowX: hidden` clips at the PADDING box (the
// same mechanism that keeps the 3.0 bubbles' tails visible), so 8px of overhang
// survives and 9px would be shaved. An 18px badge would need that 9px. No outer
// ring, for the same reason.
const HITL7_BADGE_PX = 16;

const HITL7_BOX_STYLE: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  // Vertically centred against the text, however many lines it wraps to.
  alignItems: 'center',
  gap: '8px',
  // No top margin: MessageContainer already ends in a 10px spacer, so one here
  // stacked on top of it and read as a gap twice the size of any other in the
  // pane.
  // Left inset clears the half of the badge that sits inside the box.
  padding: '6px 6px 6px 14px',
  backgroundColor: HITL7_FILL,
  // Dashed, so the box reads as provisional — something offered, not something
  // said. Every other box in the transcript is solid or has no border at all.
  border: `1px dashed ${Colors.botticelli}`,
  borderRadius: '8px',
};

const HITL7_BADGE_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: `-${HITL7_BADGE_PX / 2}px`,
  top: '50%',
  transform: 'translateY(-50%)',
  width: `${HITL7_BADGE_PX}px`,
  height: `${HITL7_BADGE_PX}px`,
  borderRadius: '50%',
  backgroundColor: HITL7_COPILOT_COLOR,
  color: Colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const HITL7_BADGE_ICON_STYLE: React.CSSProperties = { fontSize: '10px' };

const HITL7_TEXT_STYLE: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  fontSize: HITL3_FONT_SIZE,
  fontWeight: 500,
  color: HITL7_COPILOT_COLOR,
  // Wraps to as many lines as it needs. Explicitly never truncated: a clipped
  // suggestion is worse than none, because the agent cannot tell what they
  // would be saying before they accept it.
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

// Solid green tick / red cross, applied through `sx` rather than the makeStyles
// classes the 3.0 box uses. That is not a style preference: MUI's own IconButton
// emotion styles outrank makeStyles for `background-color`, so the classes'
// fills were losing and the buttons rendered as a bare tick and a bare cross —
// the dismiss one a light grey glyph on a grey box, effectively invisible.
// `sx` is emitted as emotion, so it wins on the same terms.
//
// The fills are the order buttons' own: `abbey` is the Finish Order background
// and `torchRed` is what `color="error"` resolves to on Cancel Order. Accepting
// and dismissing a suggestion therefore carry the same yes/no colours the agent
// already acts on at the other end of the console.
//
// The GLYPHS differ, because the two fills are not equally dark. White on the
// red is 3.9:1 and clears the 3:1 non-text minimum. White on the green is only
// 1.87:1 — that bright yellow-green is very light — which would put a barely
// visible tick back on the button, the exact problem this change is fixing. The
// tick is therefore near-black, at 8.3:1, the same dark-on-light treatment the
// amber Conversation intent pills already use.
const HITL7_ACCEPT_SX = {
  p: 0.5,
  backgroundColor: Colors.abbey,
  color: Colors.primaryButtonTextColor,
  '&:hover': { backgroundColor: Colors.abbey },
} as const;

const HITL7_DISMISS_SX = {
  p: 0.5,
  backgroundColor: Colors.torchRed,
  color: Colors.white,
  '&:hover': { backgroundColor: Colors.torchRed },
} as const;

const HITL7_ACTIONS_STYLE: React.CSSProperties = {
  display: 'flex',
  gap: '4px',
  flexShrink: 0,
  alignSelf: 'center',
};

const useStyles = makeStyles((theme: Theme) => ({
  ask: {
    // 8px inset on all sides; the bottom margin also keeps the ask off the
    // pinned AI status band at the bottom of the Pulse card.
    marginTop: HITL3_PADDING,
    marginBottom: HITL3_PADDING,
    marginLeft: HITL3_PADDING,
    marginRight: HITL3_PADDING,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: '#eef1fb',
    border: '1px solid #c5cae9',
    borderRadius: '8px',
    padding: theme.spacing(0.875, 1.125),
  },
  // Match the "Welcome" intent pill's text: medium weight, shared hitl3 size.
  q: {
    flex: 1,
    minWidth: 0,
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    color: Colors.readBack,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  okBtn: {
    backgroundColor: Colors.green,
    color: Colors.white,
    '&:hover': { backgroundColor: Colors.green },
  },
  noBtn: {
    backgroundColor: Colors.white,
    color: '#79808a',
    border: `1px solid ${Colors.alto}`,
  },
}));

const PulseAdvise = () => {
  const classes = useStyles();

  // The co-pilot advise loop is gated by the agent_cart flag, matching the
  // standalone AISuggestions surface.
  const isAgentCartEnabled = useSelector(selectFeatureFlagAgentCart);
  // HITL-1663: the 7.0 Pulse strip is one wrapping flex row, so the ask has to
  // claim a full row of its own rather than queue up beside the chips, and its
  // own 8px margins have to go — the strip already insets its content, and two
  // 8px gaps stacked read as a gap twice the size of every other one.
  const isHitl7 = useSelector(selectIsHitl7Enabled);
  // HITL-1534: a suggestion is something to SAY to the customer at the menu
  // board, so once the car has pulled away it is dead by definition — accepting
  // it would speak the departed customer's line (their total) at whoever is at
  // the board next. The slice reset on car_enter handles the next session; this
  // covers the window between car_exit and that car_enter.
  //
  // Deliberately a DISPLAY gate rather than a car_exit reset of the slice:
  // clearing AI state on car_exit caused a prod incident (dropped order + cart
  // revert) because a car can leave the board while the same session's order is
  // still being finished — see the note at CustomerPulse/index.tsx. Hiding the
  // card destroys nothing, so a session that outlives the car keeps its state.
  const isCarPresent = useSelector(selectIsCarPresent);
  const { activeSuggestion, accept, dismiss, getDisplayText } =
    useAISuggestionActions();
  const showAsk = isAgentCartEnabled && isCarPresent && !!activeSuggestion;

  // Alt+A / Alt+D mirror the Accept / Dismiss buttons (shared path via
  // useAISuggestionActions). `event.code` is matched (not `event.key`) so macOS
  // Option-key glyphs don't break the match; capture phase so it fires even
  // while the omnibar is focused. No-ops unless the co-pilot is enabled and a
  // suggestion is showing.
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    if (e.code !== 'KeyA' && e.code !== 'KeyD') {
      return;
    }
    // Gated on `showAsk`, not just the flag + suggestion: the shortcut must not
    // reach a card the agent cannot see. Without the car-presence check here the
    // hidden post-car_exit suggestion would still be acceptable by keyboard
    // (HITL-1534), which is the harmful half of the bug.
    if (!showAsk || !activeSuggestion) {
      return;
    }
    e.preventDefault();
    // stopPropagation, unlike Alt+1…9 in useIntentHotkeys, which deliberately lets
    // the take-over guard see the press first. Not an inconsistency: the rule is not
    // "speaking to the customer prompts", it is "acting on your OWN initiative
    // prompts". Accepting a co-pilot card ratifies the AI's own line — it goes out
    // stamped `intent: 'ai_suggestion'` and the AI keeps driving — where Alt+1 is an
    // utterance the agent chose, which is a takeover. Prompting here would ask the
    // agent to confirm taking over from the AI in order to agree with it.
    e.stopPropagation();
    if (e.code === 'KeyA') {
      accept(activeSuggestion);
    } else {
      dismiss(activeSuggestion);
    }
  };
  useEventListener('keydown', handleKeyDown, window, true);

  if (!showAsk || !activeSuggestion) {
    return null;
  }

  const actions = (
    <>
      <IconButton
        className={isHitl7 ? undefined : classes.okBtn}
        size="small"
        title="Accept (Alt+A)"
        aria-label="Accept suggestion"
        onClick={() => accept(activeSuggestion)}
        sx={isHitl7 ? HITL7_ACCEPT_SX : { p: 0.5 }}
      >
        <CheckIcon fontSize="small" />
      </IconButton>
      <IconButton
        className={isHitl7 ? undefined : classes.noBtn}
        size="small"
        title="Dismiss (Alt+D)"
        aria-label="Dismiss suggestion"
        onClick={() => dismiss(activeSuggestion)}
        sx={isHitl7 ? HITL7_DISMISS_SX : { p: 0.5 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  if (isHitl7) {
    return (
      <div
        style={HITL7_BOX_STYLE}
        data-testid="pulse-advise"
        // The transcript files an "ASR MISSING" issue report from a click on
        // its own blank area, and this box now lives INSIDE that click target.
        // Without this marker every Accept would quietly file a spurious
        // Missing Transcription report alongside sending the line. Matched with
        // closest() in LiveTranscriptCard, so the buttons are covered too.
        data-suggestion="true"
      >
        <span style={HITL7_BADGE_STYLE} data-testid="pulse-advise-badge">
          <AutoAwesomeOutlinedIcon style={HITL7_BADGE_ICON_STYLE} />
        </span>
        <span style={HITL7_TEXT_STYLE}>{getDisplayText(activeSuggestion)}</span>
        <div style={HITL7_ACTIONS_STYLE}>{actions}</div>
      </div>
    );
  }

  return (
    <div className={classes.ask} data-testid="pulse-advise">
      {/* HITL-1429: the AI passes the brand menu's own prompt through
          verbatim, so the title can carry `{total}` / `{RESTAURANT_NAME}`
          placeholders. Render it resolved (behind the flag) so the agent reads
          the same line the customer will hear, not raw braces. */}
      <span className={classes.q}>{getDisplayText(activeSuggestion)}</span>
      <div className={classes.actions}>{actions}</div>
    </div>
  );
};

export default PulseAdvise;
